// src/auth/AuthProvider.tsx
"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { generateMockInitData } from "@/utils/telegram/generate-mock-init-data";

import { initData, isTMA } from "@tma.js/sdk-react";

type AuthState =
  | { status: "idle" | "loading" }
  | { status: "unauthorized_environment" }
  | {
      status: "authenticated";
      user: { telegram_id: string; username?: string; language_code?: string };
      accessToken: string;
      expiresAt: number; // Unix timestamp
    }
  | { status: "error"; error: string };

type AuthContextValue = {
  state: AuthState;
  reload: () => Promise<void>;
  logout: () => void;
  /**
   * Gets the current valid access token.
   * Returns null if not authenticated or token expired.
   */
  getAccessToken: () => string | null;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Calls backend to validate initData and receive JWT access token.
 */
async function authenticate(initData: string): Promise<{
  ok: boolean;
  access_token?: string;
  expires_in?: number;
  error?: string;
}> {
  const baseURL = import.meta.env.VITE_API_BASE_URL || "";
  const res = await fetch(`${baseURL}/auth`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ initData }),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP ${res.status}`);
  }

  return res.json();
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<AuthState>({ status: "idle" });
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRefreshingRef = useRef<boolean>(false);

  /**
   * Authenticates user with backend and schedules token refresh.
   * @param isRefresh - If true, this is a background refresh and we won't show loading state
   */
  const doAuth = async (isRefresh = false) => {
    // Prevent concurrent refresh attempts
    if (isRefresh && isRefreshingRef.current) {
      console.log("[Auth] Refresh already in progress, skipping");
      return;
    }

    if (isRefresh) {
      isRefreshingRef.current = true;
    }

    try {
      // Only show loading state if this is initial auth, not a background refresh
      if (!isRefresh) {
        setState({ status: "loading" });
      }

      const isTelegramMiniAppEnvironment = isTMA();

      console.log("doAuth");
      console.log(`.VITE_API_BASE_URL: ${import.meta.env.VITE_API_BASE_URL}`);

      let initRawData: string | undefined;

      const testInitData = initData;
      console.log("testInitData", testInitData);

      if (isTelegramMiniAppEnvironment) {
        console.log("🤖 Telegram mini app environment, ");
        // try from window
        initRawData = (window as any).Telegram?.WebApp?.initData;

        console.log("initRawData Window", initRawData);
      } else {
        console.log("not telegram mini app environment, generateMockInitData");
        initRawData = await generateMockInitData();
      }

      console.log(`initRawData: ${initRawData}`);

      if (!initRawData) {
        setState({
          status: "error",
          error:
            "initData not found. Open inside Telegram Mini App or provide ?initData= for dev mode.",
        });
        return;
      }

      // 2) Validate with backend and get JWT access token
      const json = await authenticate(initRawData as string);

      if (!json.ok || !json.access_token) {
        setState({
          status: "error",
          error: json.error || "Authentication failed",
        });
        return;
      }

      // 3) Parse JWT to extract user info (without verification - backend already validated)
      const payloadBase64 = json.access_token.split(".")[1]!;
      const payload = JSON.parse(atob(payloadBase64));

      const expiresIn = json.expires_in || 900; // Default 15min
      const expiresAt = Math.floor(Date.now() / 1000) + expiresIn;

      setState({
        status: "authenticated",
        user: {
          telegram_id: payload.telegram_id,
          username: payload.username,
          language_code: payload.language_code,
        },
        accessToken: json.access_token,
        expiresAt,
      });

      // 4) Schedule token refresh 1 minute before expiration
      scheduleTokenRefresh(expiresIn);
    } catch (e: any) {
      console.error("Authentication error:", e);

      if (e?.message === "Missing required environment variables") {
        setState({ status: "unauthorized_environment" });
        return;
      }

      // On refresh failure, don't change state to error - keep using existing token
      if (!isRefresh) {
        setState({
          status: "error",
          error: e?.message || "Authentication exception",
        });
      } else {
        console.warn(
          "[Auth] Background refresh failed, will retry on next scheduled refresh",
        );
        // Re-schedule a retry in 30 seconds
        scheduleTokenRefresh(30);
      }
    } finally {
      isRefreshingRef.current = false;
    }
  };

  /**
   * Schedules automatic token refresh before expiration.
   */
  const scheduleTokenRefresh = (expiresIn: number) => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    // Refresh 10 minutes before expiration (or halfway if token is short-lived)
    const refreshIn = Math.max(expiresIn - 600, expiresIn / 2);

    console.log(`[Auth] Scheduling token refresh in ${refreshIn} seconds`);

    refreshTimerRef.current = setTimeout(() => {
      console.log("[Auth] Auto-refreshing access token...");
      doAuth(true); // Background refresh - don't show loading state
    }, refreshIn * 1000);
  };

  useEffect(() => {
    // Authenticate on mount
    doAuth();

    // Cleanup timer on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      reload: doAuth,
      logout: () => {
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
        }
        setState({ status: "idle" });
        if (typeof window !== "undefined") window.location.reload();
      },
      getAccessToken: () => {
        if (state.status !== "authenticated") return null;
        // Always return the token - scheduled refresh handles renewal
        return state.accessToken;
      },
    }),
    [state],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access auth state and methods.
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

/**
 * Fetches with authentication and automatic request/response logging
 */
export async function fetchWithAuth(
  url: string,
  accessToken: string,
  options: RequestInit = {},
): Promise<Response> {
  const baseURL = import.meta.env.VITE_API_BASE_URL || "";
  const fullUrl = url.startsWith("http") ? url : `${baseURL}${url}`;

  const headers = new Headers(options.headers);
  headers.set("Authorization", `Bearer ${accessToken}`);

  const requestTime = Date.now();

  // Log request details
  console.log("📤 API Request:", {
    method: options.method || "GET",
    url: fullUrl,
    body: options.body ? JSON.parse(options.body as string) : undefined,
    timestamp: new Date().toISOString(),
  });

  try {
    const response = await fetch(fullUrl, {
      ...options,
      headers,
    });

    const responseTime = Date.now() - requestTime;

    // Clone response to read body without consuming it
    const clonedResponse = response.clone();
    const responseData = await clonedResponse.json().catch(() => null);

    // Log response details
    console.log("📥 API Response:", {
      method: options.method || "GET",
      url: fullUrl,
      status: response.status,
      statusText: response.statusText,
      data: responseData,
      duration: `${responseTime}ms`,
      timestamp: new Date().toISOString(),
    });

    return response;
  } catch (error) {
    const errorTime = Date.now() - requestTime;

    // Log error details
    console.error("❌ API Error:", {
      method: options.method || "GET",
      url: fullUrl,
      error: error instanceof Error ? error.message : String(error),
      duration: `${errorTime}ms`,
      timestamp: new Date().toISOString(),
    });

    throw error;
  }
}

export const AuthGate: React.FC<{
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ children, fallback }) => {
  const { state } = useAuth();

  if (state.status === "loading" || state.status === "idle") {
    return fallback ?? <div style={{ padding: 16 }}>Authenticating…</div>;
  }

  if (state.status === "error") {
    return (
      <div style={{ padding: 16 }}>
        <h3>Auth error</h3>
        <p>{state.error}</p>
      </div>
    );
  }

  return <>{children}</>;
};
